import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, Send, Sparkles, User, Bot, X } from 'lucide-react';
import { API } from '../utils/api';

// Wine Sommelier Chatbot Component
const WineChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your Wine Sommelier. I can help you find the perfect wine based on your preferences, budget, or food pairing needs. What are you looking for today?"
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId,
        history: messages.slice(-6).map(m => ({ role: m.role, content: m.content }))
      });

      if (response.data.success) {
        setSessionId(response.data.session_id);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.response,
          recommendations: response.data.recommended_wines
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    "Best red wine under $15?",
    "Wine for steak dinner?",
    "Good white for seafood?",
    "Top rated Italian wines?"
  ];

  // Listen for mobile chat button click
  useEffect(() => {
    const handleOpenChat = () => setIsOpen(true);
    window.addEventListener('openWineChat', handleOpenChat);
    return () => window.removeEventListener('openWineChat', handleOpenChat);
  }, []);

  return (
    <>
      {/* Floating Chat Button - Desktop Only */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close Wine Sommelier chat' : 'Open Wine Sommelier chat'}
        className="hidden md:flex fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-[#722F37] to-[#8B3A42] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 items-center justify-center group"
        data-testid="chat-bubble-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed md:bottom-24 md:right-6 md:w-96 md:h-[500px] bottom-0 right-0 left-0 top-0 md:top-auto md:left-auto z-50 bg-white md:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#722F37] to-[#8B3A42] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Wine Sommelier</h3>
                <p className="text-xs text-white/80">AI-powered recommendations</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
              className="md:hidden p-2 hover:bg-white/10 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 bg-[#722F37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-[#722F37] text-white rounded-br-sm'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.recommendations && msg.recommendations.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium text-gray-500">Recommended wines:</p>
                      {msg.recommendations.slice(0, 3).map((wine, i) => (
                        <Link
                          key={i}
                          to={`/${wine.id}`}
                          onClick={() => setIsOpen(false)}
                          className="block p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                        >
                          <p className="text-xs font-medium text-[#722F37]">{wine.name}</p>
                          <p className="text-xs text-gray-500">${wine.price} · {wine.country}</p>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="w-8 h-8 bg-[#722F37] rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white p-3 rounded-2xl rounded-bl-sm shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions - Only show when no user messages yet */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex gap-2 flex-wrap bg-gray-50">
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInputMessage(q);
                    setTimeout(() => sendMessage(), 100);
                  }}
                  className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-[#722F37] hover:text-[#722F37] transition"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about wines..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-[#722F37] focus:ring-1 focus:ring-[#722F37]"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="w-10 h-10 bg-[#722F37] text-white rounded-full flex items-center justify-center hover:bg-[#8B3A42] disabled:opacity-50 disabled:cursor-not-allowed transition"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WineChatBot;
